/**
 * Modern Modal System
 * Supports multiple modals, escape key, click away, scroll lock
 *
 * API:
 * ModalSystem.open( 'modal-id' );
 * ModalSystem.close( 'modal-id' );
 * ModalSystem.closeAll();
 *
 */

class ModalSystem {
	constructor() {
		this.activeModals = [];
		this.scrollPosition = 0;
		this.init();
	}

	init() {
		this.bindEvents();
	}

	bindEvents() {
		// Handle escape key
		document.addEventListener( 'keydown', ( e ) => {
			if ( e.key === 'Escape' && this.activeModals.length > 0 ) {
				this.close( this.activeModals[ this.activeModals.length - 1 ] );
			}
		} );

		// Handle click events for modal triggers and close buttons
		document.addEventListener( 'click', ( e ) => {
			// Modal trigger
			const trigger = e.target.closest( '[data-modal-trigger]' );
			if ( trigger ) {
				e.preventDefault();
				const targetId = trigger.getAttribute( 'data-modal-trigger' );
				this.open( targetId );
			}

			// Modal close
			if (
				e.target.matches( '[data-modal-close]' ) ||
				e.target.closest( '[data-modal-close]' )
			) {
				e.preventDefault();
				const modal = e.target.closest( '.modal-overlay' );
				if ( modal ) this.close( modal.id );
			}
		} );
	}

	open( modalId ) {
		const modal = document.getElementById( modalId );
		if ( ! modal ) return;

		// Lock scroll on first modal
		if ( this.activeModals.length === 0 ) {
			this.lockScroll();
		}

		// Add to active modals
		this.activeModals.push( modalId );

		// Show modal
		modal.classList.add( 'active' );
		modal.setAttribute( 'aria-hidden', 'false' );

		// Focus management
		this.focusModal( modal );

		// Trigger custom event
		modal.dispatchEvent(
			new CustomEvent( 'modal:opened', {
				detail: { modalId },
			} )
		);
	}

	close( modalId ) {
		const modal = document.getElementById( modalId );
		if ( ! modal ) return;

		// Remove from active modals
		this.activeModals = this.activeModals.filter(
			( id ) => id !== modalId
		);

		// Hide modal
		modal.classList.remove( 'active' );
		modal.setAttribute( 'aria-hidden', 'true' );

		// Unlock scroll if no modals are active
		if ( this.activeModals.length === 0 ) {
			this.unlockScroll();
		}

		// Return focus to trigger if available
		const trigger = document.querySelector(
			`[data-modal-trigger="${ modalId }"]`
		);
		if ( trigger ) trigger.focus();

		// Trigger custom event
		modal.dispatchEvent(
			new CustomEvent( 'modal:closed', {
				detail: { modalId },
			} )
		);
	}

	closeAll() {
		[ ...this.activeModals ].forEach( ( modalId ) =>
			this.close( modalId )
		);
	}

	lockScroll() {
		this.scrollPosition = window.pageYOffset;
		document.body.style.top = `-${ this.scrollPosition }px`;
		document.body.classList.add( 'modal-open' );
	}

	unlockScroll() {
		document.body.classList.remove( 'modal-open' );
		document.body.style.top = '';
		window.scrollTo( 0, this.scrollPosition );
	}

	focusModal( modal ) {
		// Focus first focusable element or modal itself
		const focusable = modal.querySelector(
			'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
		);
		if ( focusable ) {
			focusable.focus();
		} else {
			modal.focus();
		}
	}

	// Public API methods
	static open( modalId ) {
		window.modalSystem?.open( modalId );
	}

	static close( modalId ) {
		window.modalSystem?.close( modalId );
	}

	static closeAll() {
		window.modalSystem?.closeAll();
	}
}

// Initialize when DOM is ready
if ( document.readyState === 'loading' ) {
	document.addEventListener( 'DOMContentLoaded', () => {
		window.modalSystem = new ModalSystem();
	} );
} else {
	window.modalSystem = new ModalSystem();
}

// Export for module usage
if ( typeof module !== 'undefined' && module.exports ) {
	module.exports = ModalSystem;
}
